import React from 'react';
import { cn } from '@/lib/utils';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shape variant */
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  /** Width (CSS value or number in px) */
  width?: string | number;
  /** Height (CSS value or number in px) */
  height?: string | number;
  /** Animation type */
  animation?: 'pulse' | 'shimmer';
  /** Number of text lines (only for variant='text') */
  lines?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  lines = 1,
  className,
  style,
  ...props
}) => {
  const baseClasses = cn(
    animation === 'pulse' ? 'animate-skeleton' : 'animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent',
    className
  );

  const resolveSize = (val?: string | number): string | undefined => {
    if (val === undefined) return undefined;
    return typeof val === 'number' ? `${val}px` : val;
  };

  if (variant === 'circular') {
    const size = resolveSize(width) || resolveSize(height) || '40px';
    return (
      <div
        className={cn('rounded-full', baseClasses)}
        style={{
          width: size,
          height: size,
          backgroundColor: 'var(--theme-card-border)',
          ...style,
        }}
        {...props}
      />
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={cn('rounded-xl', baseClasses)}
        style={{
          width: resolveSize(width) || '100%',
          height: resolveSize(height) || '120px',
          backgroundColor: 'var(--theme-card-border)',
          ...style,
        }}
        {...props}
      />
    );
  }

  if (variant === 'rectangular') {
    return (
      <div
        className={cn('rounded-lg', baseClasses)}
        style={{
          width: resolveSize(width) || '100%',
          height: resolveSize(height) || '48px',
          backgroundColor: 'var(--theme-card-border)',
          ...style,
        }}
        {...props}
      />
    );
  }

  // variant === 'text'
  if (lines > 1) {
    return (
      <div className="space-y-2" {...props}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn('rounded-full', baseClasses)}
            style={{
              width: i === lines - 1 ? '60%' : resolveSize(width) || '100%',
              height: resolveSize(height) || '14px',
              backgroundColor: 'var(--theme-card-border)',
              ...style,
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn('rounded-full', baseClasses)}
      style={{
        width: resolveSize(width) || '100%',
        height: resolveSize(height) || '14px',
        backgroundColor: 'var(--theme-card-border)',
        ...style,
      }}
      {...props}
    />
  );
};

/** Pre-built skeleton for a table row */
export const SkeletonTableRow: React.FC<{ columns?: number }> = ({ columns = 4 }) => (
  <div className="flex items-center gap-4 px-4 py-3">
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton key={i} width={i === 0 ? '30%' : '20%'} height={14} />
    ))}
  </div>
);

/** Pre-built skeleton for a card */
export const SkeletonCard: React.FC = () => (
  <div
    className="rounded-xl border p-4 space-y-3"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      borderColor: 'var(--theme-card-border)',
    }}
  >
    <div className="flex items-center gap-3">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="flex-1 space-y-2">
        <Skeleton width="60%" height={14} />
        <Skeleton width="40%" height={12} />
      </div>
    </div>
    <Skeleton variant="text" lines={2} />
    <Skeleton variant="rectangular" height={32} />
  </div>
);

Skeleton.displayName = 'Skeleton';
SkeletonTableRow.displayName = 'SkeletonTableRow';
SkeletonCard.displayName = 'SkeletonCard';
